<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" elevation="0" title="Overall survival Kaplan-Meier estimates">
      <v-card-subtitle>Use the information icon, in the top right corner, to see the population definition, and
        use the filter icon to filter the cohort.</v-card-subtitle>
      <v-row no-gutters>
        <v-col v-for="(number, index) in survivalNumbers" :key="index">
          <number-box :number="number.value" :label="number.label"/>
        </v-col>
        <v-col>
          <v-sheet class="pa-2 ma-2">
            <div class="number-box-left">
              <transition name="fade" mode="out-in">
                <div :key="medianFollowUpTimeData">
                  <v-data-table :headers="medianFollowUpTimeHeaders" :items="medianFollowUpTimeData"
                                :fixed-header="true"
                                height="200px"
                                :items-per-page="0">
                    <template #bottom></template>
                  </v-data-table>
                </div>
              </transition>
            </div>
          </v-sheet>
        </v-col>
        <v-col>
          <v-select class="mt-10 mr-5" v-model="selected_stratify_items" label="Comparison groups"
                    :items="select_stratify_items" :multiple="true">
            <template #item="{ props }">
              <v-list-subheader v-if="props.header">{{ props.header }}</v-list-subheader>
              <v-divider v-else-if="props.divider" class="mt-2" />
              <v-list-item v-else v-bind="props" class="ml-2"></v-list-item>
            </template>
          </v-select>
          <v-checkbox v-model="kaplan_meier_confidence_intervals" label="Show confidence intervals"
                      hide-details></v-checkbox>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-card class="mx-auto" min-height="800" elevation="0">
            <GChart type="LineChart" :data="survivalChartData"
                    :options="survivalChartOptions"
                    v-if="survivalChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
          </v-card>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0" title="Cause of death in the total cohort (ICD-10 codes)">
            <v-card-subtitle class="text-wrap">Top causes of death among deceased patients with a known cause-of-death code.</v-card-subtitle>
            <v-data-table :headers="causeOfDeathIcdCodeHeaders"
                          :items="causeOfDeathIcdCodeItems"
                          density="compact"
                          :fixed-header="true"
                          :items-per-page="0"
                          class="striped-table">
              <template #bottom></template>
              <template v-slot:no-data>
                <v-progress-circular v-if="loadingCauseOfDeathTables" color="primary" indeterminate></v-progress-circular>
                <span v-else>No cause-of-death data available</span>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
        <v-col cols="0" lg="1" xl="1" xxl="1"/>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0" title="Cause of death in the total cohort (ICD-10 chapters)">
            <v-card-subtitle class="text-wrap">Top ICD-10 chapters among deceased patients with a known cause-of-death chapter.</v-card-subtitle>
            <v-data-table :headers="causeOfDeathIcdChapterHeaders"
                          :items="causeOfDeathIcdChapterItems"
                          density="compact"
                          :fixed-header="true"
                          :items-per-page="0"
                          class="striped-table">
              <template #bottom></template>
              <template v-slot:no-data>
                <v-progress-circular v-if="loadingCauseOfDeathTables" color="primary" indeterminate></v-progress-circular>
                <span v-else>No cause-of-death data available</span>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import NumberBox from "@/components/NumberBox.vue";

export default defineComponent({
  name: 'NeurologicalDiseaseOverallSurvival',
  props: {
    icd: String,
    ageAtDiagnosis: Array,
    sex: Array,
    diagnosisDate: Array,
    survivalTimeInYears: Array,
    visible: Boolean
  },
  components: {
    NumberBox,
    GChart
  },
  data: () => ({
    kaplan_meier_confidence_intervals: false,
    select_stratify_items: [],
    selected_stratify_items: ['Currently filtered'],
    survivalNumbers: [],
    medianFollowUpTimeHeaders: [
      {
        title: 'Comparison group',
        align: 'start',
        key: 'comparison_group',
      },
      {
        title: 'Median follow-up time (months)',
        align: 'end',
        key: 'median_follow_up_time',
      }
    ],
    medianFollowUpTimeData: [],
    survivalChartData: [],
    loadingCauseOfDeathTables: true,
    causeOfDeathIcdCodeHeaders: [
      {title: 'ICD-10 code', align: 'start', key: 'causeOfDeathIcd10Code', sortable: false},
      {title: 'Cause of death', align: 'start', key: 'causeOfDeath', sortable: false},
      {title: 'Number of patients', align: 'start', key: 'numberOfPatients', sortable: false},
      {title: 'Percentage', align: 'start', key: 'percentage', sortable: false}
    ],
    causeOfDeathIcdCodeItems: [],
    causeOfDeathIcdChapterHeaders: [
      {title: 'ICD-10 chapter', align: 'start', key: 'causeOfDeathIcd10Chapter', sortable: false},
      {title: 'Number of patients', align: 'start', key: 'numberOfPatients', sortable: false},
      {title: 'Percentage', align: 'start', key: 'percentage', sortable: false}
    ],
    causeOfDeathIcdChapterItems: [],
    survivalChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      hAxis: {
        title: 'Year after index date',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: 'Probability of survival',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0',
          interval: 0.25
        },
        minorGridlines: {
          count: 0
        }
      },
      intervals: { 'style': 'area' },
      tooltip: {
        isHtml: true
      }
    }
  }),
  watch: {
    selected_stratify_items() {
      this.loadDynamicData()
    },
    kaplan_meier_confidence_intervals() {
      this.loadDynamicData()
    }
  },
  methods: {
    updateSelectStratifyItems() {
      let updated_select_stratify_items = [
        {
          title: 'Currently filtered'
        },
        { props: { divider: true } },
        { props: { header: 'Age at diagnosis' } }
      ]
      this.ageAtDiagnosis.forEach(element =>
        updated_select_stratify_items.push({
          title: element
        }))
      updated_select_stratify_items.push({
        props: { divider: true }
      })
      updated_select_stratify_items.push({
        props: { header: 'Sex' }
      })
      this.sex.forEach(element =>
          updated_select_stratify_items.push({
            title: element
          }))
      updated_select_stratify_items.push({
        props: { header: 'Year of diagnosis' }
      })
      this.diagnosisDate.forEach(element =>
          updated_select_stratify_items.push({
            title: element
          }))

      this.select_stratify_items = updated_select_stratify_items
    },
    updateSelectedStratifyItems() {
      this.selected_stratify_items = this.selected_stratify_items.filter(element =>
          element === 'Currently filtered' ||
          this.ageAtDiagnosis.includes(element) || this.sex.includes(element) ||
          this.diagnosisDate.includes(element))
    },
    async loadSurvivalNumbers() {
      return axios.get('neurological-disease/survival-numbers',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(",")
            }
          });
    },
    async loadMedianFollowUpTimeData() {
      return axios.get('neurological-disease/median-follow-up-time',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              stratify_items: this.selected_stratify_items.join(",")
            }
          });
    },
    async loadSurvivalData() {
      return axios.get('neurological-disease/survival',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              sex: this.sex.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              stratify_items: this.selected_stratify_items.join(","),
              kaplan_meier_confidence_intervals: this.kaplan_meier_confidence_intervals
            }
          });
    },
    async loadCauseOfDeathIcdCodeData() {
      return axios.get('neurological-disease/cause-of-death-icd10-codes',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadCauseOfDeathIcdChapterData() {
      return axios.get('neurological-disease/cause-of-death-icd10-chapters',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadDynamicData() {
      this.updateSelectStratifyItems()
      await Promise.all([
        this.loadSurvivalNumbers(),
        this.loadMedianFollowUpTimeData(),
        this.loadSurvivalData(),
        this.loadCauseOfDeathIcdCodeData(),
        this.loadCauseOfDeathIcdChapterData()
      ]).then(responses => {
        this.survivalNumbers = responses[0].data
        this.medianFollowUpTimeData = responses[1].data
        this.survivalChartData = responses[2].data
        this.causeOfDeathIcdCodeItems = responses[3].data
        this.causeOfDeathIcdChapterItems = responses[4].data
        console.log('Dynamic data loaded: NeurologicalDiseaseOverallSurvival')
      }).finally(() => {
        this.loadingCauseOfDeathTables = false
      })
    }
  },
  mounted() {
    this.loadDynamicData()
  },
  updated() {
    if (this.visible) {
      this.updateSelectedStratifyItems()
    }
  }
});
</script>

<style scoped>
.number-box {
  justify-content: space-around;
  text-align: center;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.number-box-left {
  justify-content: space-around;
  text-align: left;
  padding: 20px;
  margin: 10px;
  flex: 1;
  border-radius: 8px;
  background-color: #364c5b;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 0 0 2px #c0c0c0; /* Elevated border effect */
}

.large-number {
  font-size: 4em;
  font-weight: bold;
}

.percentage-number {
  font-size: 2em;
  color: #b2df8a;
}

.large-number-label {
  font-size: 1em;
  margin-top: 10px;
}

.striped-table :deep(tbody tr:nth-child(odd)) {
  background-color: rgba(0, 0, 0, .10);
}
</style>
